import { useEffect, useRef, useCallback, useState } from 'react';
import { initializeEcho } from './echo';

export const useWebSocket = (jwtToken, currentUserId) => {
  const echoRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const userChannelsRef = useRef(new Map());
  const conversationChannelsRef = useRef(new Map());
  const groupChannelsRef = useRef(new Map());
  const presenceChannelsRef = useRef(new Map());
  const announcementChannelsRef = useRef(new Map());

  // Helper to enhance message with sender info
  const enhanceMessageSender = useCallback((message, userId) => {
    const enhancedMessage = { ...message };
    
    // If no sender object, create one
    if (!enhancedMessage.sender) {
      enhancedMessage.sender = {
        id: enhancedMessage.sender_id || userId,
        name: 'User',
        full_name: 'User',
        first_name: 'User',
        surname: '',
        passport_photo: ''
      };
      
      // Try to extract from localStorage for current user's messages
      if (enhancedMessage.sender_id === userId) {
        const firstName = localStorage.getItem('current_user_first_name') || 'You';
        const surname = localStorage.getItem('current_user_surname') || '';
        const fullName = `${firstName} ${surname}`.trim();
        
        enhancedMessage.sender = {
          ...enhancedMessage.sender,
          id: userId,
          name: fullName || 'You',
          full_name: fullName || 'You',
          first_name: firstName,
          surname: surname,
          passport_photo: localStorage.getItem('current_user_photo') || ''
        };
      }
    }
    
    // Ensure name fields are populated
    if (enhancedMessage.sender) {
      if (!enhancedMessage.sender.name || enhancedMessage.sender.name === 'User') {
        enhancedMessage.sender.name = enhancedMessage.sender.full_name || 
                                     `${enhancedMessage.sender.first_name || ''} ${enhancedMessage.sender.surname || ''}`.trim() || 
                                     'User';
      }
      if (!enhancedMessage.sender.full_name) {
        enhancedMessage.sender.full_name = enhancedMessage.sender.name;
      }
    }
    
    return enhancedMessage;
  }, []);

  const initialize = useCallback(() => {
    if (!jwtToken || !currentUserId) {
      return null;
    }

    try {
      if (echoRef.current) {
        echoRef.current.disconnect();
      }

      const echo = initializeEcho(jwtToken);
      echoRef.current = echo;

      echo.connector.pusher.connection.bind('connected', () => {
        setIsConnected(true);
      });

      echo.connector.pusher.connection.bind('disconnected', () => {
        setIsConnected(false);
      });

      return echo;
    } catch (error) {
      setIsConnected(false);
      return null;
    }
  }, [jwtToken, currentUserId]);

  const subscribeToUserChannel = useCallback((onNewMessage, onGroupCreated, onMemberAdded, onAnnouncementReceived) => {
    if (!echoRef.current || !currentUserId) {
      return null;
    }

    try {
      const channelName = `user.${currentUserId}`;
      
      if (userChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        userChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);
      
      // Listen for direct messages
      channel.listen('.message.sent', (event) => {
        if (event && event.message) {
          onNewMessage?.(event);
        } else if (event && event.data) {
          onNewMessage?.({ message: event.data });
        } else if (event) {
          onNewMessage?.({ message: event });
        }
      });

      // Listen for group creation events
      channel.listen('GroupCreated', (event) => {
        if (event && event.group) {
          onGroupCreated?.(event.group);
        }
      });

      // Listen for member added events
      channel.listen('MemberAdded', (event) => {
        if (event && event.group_id) {
          onMemberAdded?.(event);
        }
      });

      // Listen for announcement events
      channel.listen('.announcement.received', (event) => {
        if (event && event.announcement) {
          console.log('New announcement received via WebSocket:', event.announcement);
          onAnnouncementReceived?.(event.announcement);
          
          // Trigger browser notification
          if (Notification.permission === 'granted') {
            new Notification(event.announcement.title, {
              body: event.announcement.content?.substring(0, 100) || 'New announcement',
              icon: event.announcement.admin?.passport_photo
            });
          }
          
          // Dispatch custom event for announcements list
          window.dispatchEvent(new CustomEvent('newAnnouncement', {
            detail: { announcement: event.announcement }
          }));
          
          // Update unread count
          const currentCount = parseInt(localStorage.getItem('announcements_unread_count') || '0');
          const newCount = currentCount + 1;
          localStorage.setItem('announcements_unread_count', newCount.toString());
          window.dispatchEvent(new CustomEvent('announcementsUpdated', {
            detail: { unreadCount: newCount }
          }));
        }
      });

      userChannelsRef.current.set(channelName, channel);
      
      return channel;
    } catch (error) {
      console.error('Error subscribing to user channel:', error);
      return null;
    }
  }, [currentUserId]);

  // Subscribe to announcements channel (for admin broadcast notifications)
  const subscribeToAnnouncementsChannel = useCallback((onBroadcastSent, onBroadcastFailed) => {
    if (!echoRef.current || !currentUserId) {
      return null;
    }

    try {
      const channelName = `announcements.admin.${currentUserId}`;
      
      if (announcementChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        announcementChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);
      
      // Listen for broadcast sent events
      channel.listen('.announcement.broadcast.sent', (event) => {
        if (event && event.data) {
          console.log('Broadcast sent successfully:', event.data);
          onBroadcastSent?.(event.data);
        }
      });

      // Listen for broadcast failed events
      channel.listen('.announcement.broadcast.failed', (event) => {
        if (event && event.data) {
          console.error('Broadcast failed:', event.data);
          onBroadcastFailed?.(event.data);
        }
      });

      // Listen for delivery statistics updates
      channel.listen('.announcement.stats.updated', (event) => {
        if (event && event.data) {
          console.log('Announcement stats updated:', event.data);
        }
      });

      announcementChannelsRef.current.set(channelName, channel);
      
      return channel;
    } catch (error) {
      console.error('Error subscribing to announcements channel:', error);
      return null;
    }
  }, [currentUserId]);

  const subscribeToConversation = useCallback((conversationId, onMessageReceived, onTypingIndicator) => {
    if (!echoRef.current || !conversationId) {
      return null;
    }

    try {
      const channelName = `conversation.${conversationId}`;
      
      if (conversationChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        conversationChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);

      channel.listen('.message.sent', (event) => {
        let message = null;
        if (event && event.message) {
          message = event.message;
        } else if (event && event.data) {
          message = event.data;
        } else if (event) {
          message = event;
        }
        
        if (message) {
          message = enhanceMessageSender(message, currentUserId);
          onMessageReceived?.(message);
        }
      });

      channel.listen('.typing.indicator', (event) => {
        onTypingIndicator?.(event);
      });

      conversationChannelsRef.current.set(channelName, channel);
      
      return channel;
    } catch (error) {
      return null;
    }
  }, [currentUserId, enhanceMessageSender]);

  const subscribeToGroupChannel = useCallback((groupId, onMessageReceived, onTypingIndicator) => {
    if (!echoRef.current || !groupId) {
      return null;
    }

    try {
      const channelName = `group-chat.${groupId}`;
      
      if (groupChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        groupChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);

      // Listen for correct event name '.group.message.sent'
      channel.listen('.group.message.sent', (event) => {
        let message = null;
        // Try different possible message locations
        if (event && event.message) {
          message = event.message;
        } else if (event && event.data && event.data.message) {
          message = event.data.message;
        } else if (event && event.data) {
          message = event.data;
        } else if (event) {
          message = event;
        }
        
        if (message) {
          // Enhance message with sender info
          message = enhanceMessageSender(message, currentUserId);
          onMessageReceived?.(message);
        }
      });

      // Also listen for 'GroupMessageSent' as backup
      channel.listen('GroupMessageSent', (event) => {
        let message = null;
        if (event && event.message) {
          message = event.message;
        } else if (event && event.data) {
          message = event.data;
        }
        
        if (message && onMessageReceived) {
          message = enhanceMessageSender(message, currentUserId);
          onMessageReceived(message);
        }
      });

      // Typing indicator listener for groups
      channel.listen('.typing.indicator', (event) => {
        onTypingIndicator?.(event);
      });

      groupChannelsRef.current.set(channelName, channel);
      
      return channel;
    } catch (error) {
      return null;
    }
  }, [currentUserId, enhanceMessageSender]);

  const joinPresenceChannel = useCallback((conversationId, onHere, onJoining, onLeaving) => {
    if (!echoRef.current || !conversationId) {
      return null;
    }

    try {
      const baseChannelName = conversationId.startsWith('temp-') ? conversationId.substring(5) : conversationId;
      const channelName = `presence-conversation.${baseChannelName}`;
      
      if (presenceChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        presenceChannelsRef.current.delete(channelName);
      }

      const presenceChannel = echoRef.current.join(channelName);
      
      presenceChannel
        .here((users) => {
          onHere?.(users);
        })
        .joining((user) => {
          onJoining?.(user);
        })
        .leaving((user) => {
          onLeaving?.(user);
        });

      presenceChannelsRef.current.set(channelName, presenceChannel);
      
      return presenceChannel;
    } catch (error) {
      return null;
    }
  }, []);

  const leaveChannel = useCallback((channelName) => {
    if (echoRef.current) {
      echoRef.current.leave(channelName);
      
      userChannelsRef.current.delete(channelName);
      conversationChannelsRef.current.delete(channelName);
      groupChannelsRef.current.delete(channelName);
      presenceChannelsRef.current.delete(channelName);
      announcementChannelsRef.current.delete(channelName);
    }
  }, []);

  const leaveConversation = useCallback((conversationIdOrTempId) => {
    if (!conversationIdOrTempId) return;

    const isTempId = conversationIdOrTempId.startsWith('temp-');
    const actualId = isTempId ? conversationIdOrTempId.substring(5) : conversationIdOrTempId;

    const channelsToLeave = [
      `conversation.${actualId}`,
      `presence-conversation.${actualId}`,
      `group-chat.${actualId}`
    ];

    channelsToLeave.forEach(channelName => {
      leaveChannel(channelName);
    });
  }, [leaveChannel]);

  const disconnect = useCallback(() => {
    if (echoRef.current) {
      userChannelsRef.current.clear();
      conversationChannelsRef.current.clear();
      groupChannelsRef.current.clear();
      presenceChannelsRef.current.clear();
      announcementChannelsRef.current.clear();
      
      echoRef.current.disconnect();
      echoRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (echoRef.current) {
        disconnect();
      }
    };
  }, [disconnect]);

  return {
    initialize,
    disconnect,
    isConnected,
    
    subscribeToUserChannel,
    subscribeToAnnouncementsChannel,
    subscribeToConversation,
    subscribeToGroupChannel,
    joinPresenceChannel,
    leaveChannel,
    leaveConversation,
    
    echoInstance: echoRef.current
  };
};

export default useWebSocket;